/**
 * Create PO's Additional Charges and SAP (backend utils/poHeaderTotals.ts, Nov 2026). Each company maps charge
 * types to its SAP additional expenses in Company Management: a mapped charge goes on the SAP PO as freight,
 * the others stay PRPO estimates (in the costing and the profit margin). The header discount always goes to
 * SAP; the tax never does (SAP adds VAT itself from each line's tax code).
 */

/** Create PO's charge types, in the order the page lists them. */
export const PO_CHARGE_TYPES = [
  'OTHERS ESTIMATED CHARGES (Finance, Insurance, Installation)',
  'FREIGHT CHARGES',
  'CUSTOMS CHARGES',
  'DELIVERY ORDER',
  'LOCAL CLEARANCE CHARGES',
] as const;

/** Charge type → SAP additional expense, as the API sends it (keys upper case). */
export type ChargeExpenseCodes = Record<string, { code: number; name?: string }>;

/** The key a charge type is matched by: trimmed, upper case. */
export const chargeKey = (type: unknown): string => String(type ?? '').trim().replace(/\s+/g, ' ').toUpperCase();

/** The SAP expense a charge type goes to, or null when it stays a PRPO estimate. */
export const sapExpenseFor = (codes: ChargeExpenseCodes | null | undefined, type: unknown): { code: number; name?: string } | null =>
  (codes && codes[chargeKey(type)]) || null;

/** "SAP freight: Freight (1)" or "PRPO estimate". */
export const chargeDestination = (codes: ChargeExpenseCodes | null | undefined, type: unknown): string => {
  const expense = sapExpenseFor(codes, type);
  if (!expense) return 'PRPO estimate';
  return `SAP freight: ${expense.name ? `${expense.name} (${expense.code})` : `expense ${expense.code}`}`;
};

/** The codes as the API answers them, or {} for anything else. */
export const readChargeExpenseCodes = (raw: unknown): ChargeExpenseCodes => {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { value = null; }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: ChargeExpenseCodes = {};
  for (const [type, entry] of Object.entries(value as Record<string, any>)) {
    const code = Number(entry && typeof entry === 'object' ? entry.code : entry);
    if (Number.isInteger(code) && code > 0) out[chargeKey(type)] = { code, name: entry && typeof entry === 'object' ? String(entry.name || '') : '' };
  }
  return out;
};
